/**
 * The four href forms a Solunary marketing-site navigation link may take
 * (docs/hq-site-module/hq-site-api-contract.md §3.3). The server validates every stored
 * href (422 INVALID_HREF); this module mirrors those rules for two readers:
 *
 *   - the public Navbar/footer, which needs the KIND to pick a renderer (router `<Link>`
 *     for an internal path, a plain anchor for everything else) and refuses to render an
 *     href of no known kind — a `javascript:` URL must never reach an `href` attribute,
 *     even if something upstream let one through;
 *   - the Super Admin link editor, which shows the operator why an href will be refused
 *     before the round trip. The server stays the authority.
 *
 * Pure (no React, no "@/" imports) so both planes and Jest import it for real.
 */

// Whitespace or an ASCII control character anywhere: never part of an accepted href.
// eslint-disable-next-line no-control-regex
const WHITESPACE_OR_CONTROL = /[\s\u0000-\u001f\u007f]/;
const EXTERNAL = /^https?:\/\/[^\s/?#]+/i;
const MAIL = /^mailto:\S+$/i;

/**
 * @param {string} href
 * @returns {"anchor" | "internal" | "external" | "mail" | null}
 *   `null` when the href matches none of the accepted forms.
 */
export function hrefKind(href) {
  const v = String(href ?? "").trim();
  if (v === "") return null;
  if (v.startsWith("#")) return v.length > 1 && !WHITESPACE_OR_CONTROL.test(v) ? "anchor" : null;
  if (v.startsWith("/")) {
    if (v.startsWith("//") || v.includes("\\") || WHITESPACE_OR_CONTROL.test(v)) return null;
    return "internal";
  }
  if (EXTERNAL.test(v)) return WHITESPACE_OR_CONTROL.test(v) ? null : "external";
  if (MAIL.test(v)) return "mail";
  return null;
}

/**
 * Why the server would refuse `href`, or `null` when it would accept it.
 *
 * @param {string} href
 * @param {{ anchors: string[], maxLength: number }} rules  from `GET /schema`
 *   (`anchors`, `limits.href`).
 * @returns {null | "empty" | "tooLong" | "unknownAnchor" | "invalid"}
 */
export function hrefProblem(href, { anchors, maxLength }) {
  const v = String(href ?? "").trim();
  if (v === "") return "empty";
  if (v.length > maxLength) return "tooLong";
  const kind = hrefKind(v);
  if (kind === null) return "invalid";
  if (kind === "anchor" && !anchors.includes(v.slice(1))) return "unknownAnchor";
  return null;
}
